/**
 * Hand sketch of a single-line diagram (photo of paper, whiteboard…) → board. The model reads
 * the drawing into this structure; the client builds the diagram with the generator, so the
 * output is always a clean, rule-checked schematic and the user previews it before applying.
 */
import { z } from 'zod';
import type { BoardSpec } from '@elec/editor';
import { describedCircuitSchema, describedToSpec } from './circuits.ts';

export const sketchSchema = z.object({
  mainSwitchA: z.number().positive().nullable().describe('Main switch rating, if drawn'),
  mainRcdMa: z
    .number()
    .positive()
    .nullable()
    .describe('Sensitivity of the RCD at the origin (usually 300 mA), if drawn'),
  groups: z
    .array(
      z.object({
        rcdMa: z
          .number()
          .positive()
          .nullable()
          .describe('Sensitivity of the RCD protecting this group; null = directly under the main RCD'),
        rcdType: z.enum(['AC', 'A', 'F', 'B']).nullable(),
        circuits: z.array(describedCircuitSchema),
      }),
    )
    .describe('Circuits grouped by the RCD that protects them, left to right as drawn'),
  earthOhm: z
    .number()
    .positive()
    .nullable()
    .optional()
    .describe('Earth electrode resistance in Ω, when written ("RA = 25 Ω", "terre 22 Ω")'),
  notes: z.string().describe('What was unclear or missing in the sketch'),
});
export type Sketch = z.infer<typeof sketchSchema>;

export function sketchToBoard(s: Sketch): BoardSpec {
  return {
    main: { ratingA: s.mainSwitchA ?? '', poles: 2 },
    ...(s.earthOhm ? { earth: { resistanceOhm: s.earthOhm } } : {}),
    ...(s.mainRcdMa ? { mainRcd: { ratingA: 40, sensitivityMa: s.mainRcdMa, rcdType: 'A', poles: 2 } } : {}),
    groups: s.groups
      .filter((g) => g.circuits.length)
      .map((g) => ({
        ...(g.rcdMa
          ? { rcd: { ratingA: 40, sensitivityMa: g.rcdMa, rcdType: g.rcdType ?? 'A', poles: 2 } }
          : {}),
        circuits: g.circuits.map(describedToSpec),
      })),
  };
}

const circuit = (
  label: string,
  ratingA: number,
  curve: 'B' | 'C',
  kind: 'socket' | 'light' | 'appliance',
  count: number,
  section: number,
  symbolId: string | null = null,
) => ({
  label,
  device: 'MCB' as const,
  ratingA,
  curve,
  points: [{ kind, symbolId, count }],
  cable: { type: 'XVB', cores: 3, sectionMm2: section },
  rcdMa: null,
});

/** What demo mode returns for any sketch. */
export const DEMO_SKETCH: Sketch = {
  mainSwitchA: 40,
  mainRcdMa: 300,
  groups: [
    {
      rcdMa: null,
      rcdType: null,
      circuits: [
        circuit('Prises cuisine', 20, 'C', 'socket', 5, 2.5),
        circuit('Éclairage', 16, 'B', 'light', 6, 1.5),
        circuit('Four', 20, 'C', 'appliance', 1, 2.5, 'iec.oven'),
      ],
    },
    {
      rcdMa: 30,
      rcdType: 'A',
      circuits: [
        circuit('Salle de bain', 20, 'C', 'socket', 2, 2.5),
        circuit('Lave-linge', 20, 'C', 'appliance', 1, 2.5, 'iec.washing-machine'),
      ],
    },
  ],
  notes:
    'Démo : esquisse d’exemple, l’image n’a pas été analysée. / Demo: sample sketch, the image was not analysed.',
};
